import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Form, Row, Col, Button, Card, Alert, Spinner } from "react-bootstrap";
import DriverSelector from "../../components/driverSelector";
import { useNavigate } from "react-router-dom";

const DriverInvoiceUploadPage = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");
  const [errors, setErrors] = useState({ driver: "", file: "" });
  const [newInvoice, setNewInvoice] = useState({
    driver_id: "",
    gross_amount: "",
    note: "",
    file: null,
  });
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [redirectAfterUpload, setRedirectAfterUpload] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("driverInvoices_redirectAfterUpload");
    if (stored === "true") {
      setRedirectAfterUpload(true);
    }
  }, []);

  const handleNewInvoiceChange = (field, value) => {
    setNewInvoice((prev) => ({ ...prev, [field]: value }));
    if (field === "driver_id") setErrors((e) => ({ ...e, driver: "" }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setNewInvoice((prev) => ({ ...prev, file }));
    setErrors((e) => ({ ...e, file: "" }));
  };

  const validate = () => {
    const driverId = selectedDriver?.value ?? newInvoice.driver_id;
    const err = {
      driver: !driverId ? "Оберіть водія" : "",
      file: !newInvoice.file ? "Додайте фото або скан фактури" : "",
    };
    setErrors(err);
    return !err.driver && !err.file;
  };

  const handleCreateInvoice = async (event) => {
    event.preventDefault();
    setCreateError("");
    setCreateSuccess("");

    if (!validate()) return;

    const driverId = selectedDriver?.value || newInvoice.driver_id;
    const gross = newInvoice.gross_amount === "" || newInvoice.gross_amount == null
      ? 0
      : Number(newInvoice.gross_amount);

    try {
      setCreating(true);
      const token = localStorage.getItem("token");

      const payload = {
        driver_id: driverId,
        gross_amount: gross,
        status: "uploaded",
        source: "admin",
        note: newInvoice.note || null,
      };

      const createRes = await axios.post("/api/driver-invoices", payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!createRes.data?.success || !createRes.data?.id) {
        throw new Error(createRes.data?.error || "Не вдалося створити фактуру");
      }

      const createdId = createRes.data.id;

      const formData = new FormData();
      formData.append("image", newInvoice.file);

      await axios.post(`/api/driver-invoices/${createdId}/image`, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
      });

      setCreateSuccess("Фактуру успішно створено.");
      setNewInvoice({
        driver_id: "",
        gross_amount: "",
        note: "",
        file: null,
      });
      setSelectedDriver(null);
      setErrors({ driver: "", file: "" });

      if (redirectAfterUpload) {
        navigate("/admin/driver-invoices");
      }
    } catch (err) {
      console.error("Error creating driver invoice:", err);
      setCreateError(err.response?.data?.error || err.message || "Помилка створення фактури");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-0">
        <h2 className="mb-0">Додати фактуру водія</h2>
        <Link to="/admin/driver-invoices">
          <Button variant="outline-secondary" size="sm">Назад до списку</Button>
        </Link>
      </div>

      <Card className="mt-4 shadow-sm">
        <Card.Header as="h5" className="bg-white py-3">
          Нова фактура
        </Card.Header>
        <Card.Body className="p-4">
          {createError && (
            <Alert variant="danger" dismissible onClose={() => setCreateError("")}>
              {createError}
            </Alert>
          )}
          {createSuccess && (
            <Alert variant="success" dismissible onClose={() => setCreateSuccess("")}>
              {createSuccess}
            </Alert>)}

          <Form onSubmit={handleCreateInvoice}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Водій <span className="text-danger">*</span></Form.Label>
                  <DriverSelector
                    selectedDriver={selectedDriver}
                    setSelectedDriver={(option) => {
                      setSelectedDriver(option);
                      handleNewInvoiceChange("driver_id", option ? option.value : "");
                    }}
                  />
                  {errors.driver && (
                    <Form.Text className="text-danger d-block mt-1">{errors.driver}</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Фото / скан фактури <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className={errors.file ? "border-danger" : ""}
                  />
                  {errors.file && (
                    <Form.Text className="text-danger d-block mt-1">{errors.file}</Form.Text>
                  )}
                  {newInvoice.file && (
                    <Form.Text className="text-muted d-block mt-1">
                      Обрано: {newInvoice.file.name}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-4" />

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium">Брутто (PLN)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={newInvoice.gross_amount}
                    onChange={(e) => handleNewInvoiceChange("gross_amount", e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-1">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-medium">Нотатка</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Необовʼязково"
                    value={newInvoice.note}
                    onChange={(e) => handleNewInvoiceChange("note", e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="redirect-after-upload"
                  label="Після завантаження перейти до списку фактур"
                  checked={redirectAfterUpload}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRedirectAfterUpload(checked);
                    localStorage.setItem(
                      "driverInvoices_redirectAfterUpload",
                      checked ? "true" : "false"
                    );
                  }}
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <Link to="/admin/driver-invoices">
                <Button type="button" variant="outline-secondary">Скасувати</Button>
              </Link>
              <Button type="submit" variant="success" disabled={creating}>
                {creating ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                Додати фактуру
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DriverInvoiceUploadPage;

